const moment = require('moment');
const { template } = require('lodash');

const UTF8_BOM_STR = '\ufeff';

const TRUE_REGEXP = /^(t(rue)?|y(es)?|on|1)$/i;
const FALSE_REGEXP = /^(f(alse)?|n(o)?|off|0)$/i;

const toString = (str, fallback) => {
  if (str !== undefined) {
    if (typeof str === 'string') {
      return str;
    }
  }
  return typeof fallback === 'function' ? fallback(str) : fallback;
};

const toInt = (str, fallback) => {
  if (str !== undefined) {
    if (Number.isInteger(str)) {
      return str;
    }
    // avoid parseInt() heuristic
    const result = parseFloat(str);
    if (Number.isInteger(result)) {
      return result;
    }
  }
  return typeof fallback === 'function' ? fallback(str) : fallback;
};

const toNumber = (str, fallback) => {
  if (str !== undefined) {
    if (Number.isFinite(str)) {
      return str;
    }
    const result = parseFloat(str);
    if (Number.isFinite(result)) {
      return result;
    }
  }
  return typeof fallback === 'function' ? fallback(str) : fallback;
};

const toBoolean = (str, fallback) => {
  if (str !== undefined) {
    // if (typeof number === 'boolean') {
    if (str === true || str === false) {
      return str;
    }
    if (TRUE_REGEXP.test(str)) {
      return true;
    }
    if (FALSE_REGEXP.test(str)) {
      return false;
    }
  }
  return typeof fallback === 'function' ? fallback(str) : fallback;
};

const toDate = (str, fallback) => {
  if (str !== undefined) {
    if (str instanceof Date) {
      return str;
    }
    // avoid moment() heuristic
    if (typeof str === 'string') {
      const result = moment(str);
      if (result.isValid()) {
        return result.toDate();
      }
    }
  }
  return typeof fallback === 'function' ? fallback(str) : fallback;
};

const toDateFormatString = (str, format = 'YYYY-MM-DD', fallback) => {
  if (str !== undefined) {
    const result = moment(str);
    if (result.isValid()) {
      return result.format(format);
    }
  }
  return typeof fallback === 'function' ? fallback(str) : fallback;
};

const split = (str, sep = ',') =>
  typeof str === 'string' && str.length > 0
    ? str
        .split(sep)
        .map((text) => text.trim())
        .filter((text) => text.length > 0)
    : [];

const join = (strArray, sep = ',') =>
  Array.isArray(strArray) && strArray.length > 0
    ? strArray
        .map((it) => it.trim())
        .filter((it) => it.length > 0)
        .join(sep)
    : '';

const splitTags = (str, sep = ',') =>
  (str || '')
    .split(sep)
    .map((text) => text.trim())
    .filter((text) => text.length > 0)
    .map((text) => ({ text }));

const joinTags = (tags, sep = ',') =>
  (tags || [])
    .map((tag) => tag.text.trim())
    .filter((text) => text.length > 0)
    .join(sep);

/**
 * @deprecated in favor of URLSearchParams
 * @see https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams
 * @see https://nodejs.org/api/url.html#url_constructor_new_urlsearchparams_string
 */
const encodeQueryString = (params, paramSeparator = '&', nameValueSeparator = '=') => {
  if (typeof params !== 'object' || !params) {
    return '';
  }
  return Object.keys(params)
    .map((name) => encodeURIComponent(name) + nameValueSeparator + encodeURIComponent(params[name]))
    .join(paramSeparator);
};

/**
 * @deprecated in favor of URLSearchParams
 * @see https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams
 * @see https://nodejs.org/api/url.html#url_constructor_new_urlsearchparams_string
 */
const decodeQueryString = (str, paramSeparator = '&', nameValueSeparator = '=') => {
  if (typeof str !== 'string' || !str) {
    return {};
  }
  return str.split(paramSeparator).reduce((params, param) => {
    const [name, value] = param.split(nameValueSeparator);
    // eslint-disable-next-line no-param-reassign
    params[name] = value;
    return params;
  }, {});
};

const normalizePhoneNumber = (str, fallback) => {
  if (typeof str !== 'string' || !str) {
    if (fallback && typeof fallback === 'function') {
      return fallback();
    }
    if (fallback && typeof fallback === 'string') {
      return fallback;
    }
    return '';
  }

  // fast return for normal string
  if (str.match(/^01\d{9}$/)) {
    return str;
  }
  // national number
  if (str.match(/^\+820?1|^0820?1\d{9}$/)) {
    const trimmedNumber = str.slice(-9);
    return `01${trimmedNumber}`;
  }
  // XXX: 이렇게 까지 배려해야 할까?
  const trimmedString = str.split(/\D/).join('');
  // fast return for normal string
  if (trimmedString.match(/^01\d{9}$/)) {
    return trimmedString;
  }
  // national number
  if (trimmedString.match(/^820?1|^0820?1\d{9}$/)) {
    const trimmedNumber = trimmedString.slice(-9);
    return `01${trimmedNumber}`;
  }

  return str;
};

const phoneValidator = (str) => /^0[1,7][0-9]{8,9}$/.test(str.trim());
// ref https://html.spec.whatwg.org/multipage/input.html#valid-e-mail-address
const emailValidator = (str) =>
  /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(
    str.trim()
  );

// TODO: more robust impl.
const escapeCsv = (s) => {
  return s ? `"${s}"` : '';
};

/*
  @param
  @data : 전체 문자열
  @str : 전체 문자열 중에 글자가 잘려도 되는 문자열
 */
const compactTextMessage = (data, str, maxLength = 90) => {
  if (getByteLength(data) <= maxLength) {
    return data;
  }

  const tempLength = getByteLength(data.replace(str, ''));
  return data.replace(str, substringByte(str, maxLength - 3 - tempLength) + '...');
};

const convertCurrency = (val, unit = '') => {
  if (Number.isFinite(val)) {
    return `${unit} ${val.toLocaleString()}`;
  }
  return val;
};

// byte length of string 가져오는 함수
// 속도가 가장 빠른방식이라 하지만.. 함수가 알아보기 힘듬..
// TODO : 좀 더 읽기 좋고 성능좋은 함수로 교체
// 출처 : https://programmingsummaries.tistory.com/239
const getByteLength = (s, b, i, c) => {
  for (b = i = 0; (c = s.charCodeAt(i++)); b += c >> 11 ? 2 : 1);
  return b;
};

const substringByte = (str, len) => {
  let count = 0;
  let i, strLen;

  for (i = 0, strLen = str.length; i < strLen; i++) {
    if (escape(str.charAt(i)).length >= 4) {
      count += 2;
    } else {
      if (escape(str.charAt(i)) !== '%0D') {
        count++;
      }
    }

    if (count > len) {
      if (escape(str.charAt(i)) == '%0A') {
        i--;
      }
      break;
    }
  }
  return str.substring(0, i);
};

const replaceAll = (str, target, rep) => (str ? str.replace(new RegExp(target, 'g'), rep) : '');

const normalizePhone = (str) => (str ? replaceAll(str.trim(), '-', '') : '');

const normalizeCsvLineBreak = (str) => (str ? str.replace(/[\s"]+/g, ' ') : '');

const separateColonTitles = (str) => {
  if (!str) {
    return ['', ''];
  }
  const separatedTitles = str.split(':');
  if (separatedTitles.length > 1) {
    return separatedTitles;
  } else {
    separatedTitles.unshift('');
    return separatedTitles;
  }
};

const interpolate = (view, model) => {
  return template(view)(model);
};

const nonce = (len = 8) => Math.random().toString(16).substr(-len);

module.exports = {
  UTF8_BOM_STR,
  toString,
  toInt,
  toNumber,
  toBoolean,
  toDate,
  toDateFormatString,
  split,
  join,
  splitTags,
  joinTags,
  encodeQueryString,
  decodeQueryString,
  normalizePhoneNumber,
  phoneValidator,
  emailValidator,
  escapeCsv,
  compactTextMessage,
  convertCurrency,
  replaceAll,
  normalizePhone,
  normalizeCsvLineBreak,
  separateColonTitles,
  interpolate,
  nonce,
};
